import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { getToken } from 'src/auth';
import { getUser } from 'src/user';
import { getAccount } from 'src/account';
import { OverviewTab } from '../components/OverviewTab';
import { FIAdminOverviewTab } from '../components/FIAdminOverviewTab';
import { MerchantAdminOverviewTab } from '../components/MerchantAdminOverviewTab';
import { fetchPlatformMetrics, fetchFIMetrics, fetchFIMerchantMetrics, fetchMerchantTerminals, fetchMerchantSummary, fetchPaymentIssues, createMerchantTerminal, updateMerchantTerminal } from '../services/reportingApi';
import { fetchMyMerchantPayouts, fetchMyISOPayouts } from '../services/payoutsApi';

function OverviewPageComponent({ token, user, account }) {
  const { dateRange } = useOutletContext();
  const navigate = useNavigate();

  // Metric cards pass a tab key; map it to the registered route
  const handleNavigateToTab = (tab) => {
    const routeMap = {
      transactions: '/solventpay/transactions',
      fis: '/solventpay/fi-config',
      merchants: '/solventpay/merchant-config',
      settings: '/solventpay/settings',
    };
    navigate(routeMap[tab] || '/solventpay/overview');
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState(null);
  const [fiMetrics, setFiMetrics] = useState([]);
  const [fiLoading, setFiLoading] = useState(true);
  // FI Admin specific state
  const [fiSummary, setFiSummary] = useState(null);
  const [merchantMetrics, setMerchantMetrics] = useState([]);
  // Merchant Admin specific state
  const [merchantSummary, setMerchantSummary] = useState(null);
  const [merchantTerminals, setMerchantTerminals] = useState([]);
  // Payment issues (shared across all views)
  const [paymentIssues, setPaymentIssues] = useState(null);
  // Self-scoped payouts (merchant admin) / commission payouts (FI admin)
  const [myPayouts, setMyPayouts] = useState([]);

  // Determine user role
  const isSuperAdmin = user?.isSuperAdmin || user?.isSolventAdmin;
  const isFIAdmin = user?.isBankAdmin || user?.isPrimaryBankAdmin;
  const isMerchantAdmin = user?.isMerchantAdmin && !isSuperAdmin && !isFIAdmin;
  const userFiId = user?.financialInstitutionId;
  const userAccountId = user?.accountId;
  const fiName = user?.financialInstitutionName;
  const accountName = user?.accountName || account?.account_business_name;

  useEffect(() => {
    if (token && dateRange) {
      if (isSuperAdmin) {
        loadSuperAdminMetrics();
      } else if (isFIAdmin && userFiId) {
        loadFIAdminMetrics();
      } else if (isMerchantAdmin && userFiId && userAccountId) {
        loadMerchantAdminMetrics();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, token, isSuperAdmin, isFIAdmin, isMerchantAdmin, userFiId, userAccountId]);

  async function loadSuperAdminMetrics() {
    setLoading(true);
    setFiLoading(true);
    setError(null);

    const params = {};
    if (dateRange && dateRange[0]) {
      params.start_date = dateRange[0].toISOString().split('T')[0];
    }
    if (dateRange && dateRange[1]) {
      params.end_date = dateRange[1].toISOString().split('T')[0];
    }

    // Fetch platform metrics
    try {
      const platform = await fetchPlatformMetrics(params, token.accessToken);
      setPlatformMetrics(platform);
    } catch (err) {
      console.error('Failed to load platform metrics:', err);
      setError(err.message || 'Failed to load platform metrics');
    } finally {
      setLoading(false);
    }

    // Fetch FI metrics (independent of platform metrics)
    try {
      const fis = await fetchFIMetrics(params, token.accessToken);
      setFiMetrics(fis);
    } catch (err) {
      console.error('Failed to load FI metrics:', err);
      // Don't set main error - FI section can show its own error state
    } finally {
      setFiLoading(false);
    }

    // Fetch payment issues
    try {
      const issues = await fetchPaymentIssues({}, token.accessToken);
      setPaymentIssues(issues);
    } catch (err) {
      console.error('Failed to load payment issues:', err);
      // Non-critical - don't set main error
    }
  }

  async function loadFIAdminMetrics() {
    setLoading(true);
    setError(null);

    const params = {
      terra_fi_id: userFiId,
    };
    if (dateRange && dateRange[0]) {
      params.start_date = dateRange[0].toISOString().split('T')[0];
    }
    if (dateRange && dateRange[1]) {
      params.end_date = dateRange[1].toISOString().split('T')[0];
    }

    try {
      const data = await fetchFIMerchantMetrics(params, token.accessToken);
      setFiSummary(data.fi_summary);
      setMerchantMetrics(data.merchants || []);
    } catch (err) {
      console.error('Failed to load FI merchant metrics:', err);
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }

    // Fetch payment issues for this FI
    try {
      const issues = await fetchPaymentIssues({ terra_fi_id: userFiId }, token.accessToken);
      setPaymentIssues(issues);
    } catch (err) {
      console.error('Failed to load payment issues:', err);
    }

    // Fetch this FI's commission payouts (non-critical)
    try {
      const data = await fetchMyISOPayouts(token.accessToken);
      setMyPayouts(data?.results || data || []);
    } catch (err) {
      console.error('Failed to load commission payouts:', err);
    }
  }

  async function loadMerchantAdminMetrics() {
    setLoading(true);
    setError(null);

    const params = {
      fi_id: userFiId,
      account_id: userAccountId,
    };
    if (dateRange && dateRange[0]) {
      params.date_from = dateRange[0].toISOString().split('T')[0];
    }
    if (dateRange && dateRange[1]) {
      params.date_to = dateRange[1].toISOString().split('T')[0];
    }

    try {
      // Fetch both summary and terminals in parallel
      const [summary, terminalsResponse] = await Promise.all([
        fetchMerchantSummary(params, token.accessToken),
        fetchMerchantTerminals({ fi_id: userFiId, account_id: userAccountId }, token.accessToken),
      ]);
      setMerchantSummary(summary);
      // API returns {merchant_id, merchant_name, terminals: [...]}
      setMerchantTerminals(terminalsResponse?.terminals || []);
    } catch (err) {
      console.error('Failed to load merchant metrics:', err);
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }

    // Fetch payment issues for this merchant
    try {
      const issues = await fetchPaymentIssues({ terra_account_id: userAccountId }, token.accessToken);
      setPaymentIssues(issues);
    } catch (err) {
      console.error('Failed to load payment issues:', err);
    }

    // Fetch this merchant's payouts (non-critical)
    try {
      const data = await fetchMyMerchantPayouts(token.accessToken);
      setMyPayouts(data?.results || data || []);
    } catch (err) {
      console.error('Failed to load payouts:', err);
    }
  }

  async function refreshMerchantTerminals() {
    try {
      const terminalsResponse = await fetchMerchantTerminals(
        { fi_id: userFiId, account_id: userAccountId },
        token.accessToken
      );
      setMerchantTerminals(terminalsResponse?.terminals || []);
    } catch (err) {
      console.error('Failed to refresh terminals:', err);
    }
  }

  // Merchant self-service: create/update a terminal, then refresh the list.
  // Save errors propagate to the terminal form modal's inline alert.
  async function handleSaveTerminal(formData, mode, terminal) {
    if (mode === 'edit' && terminal) {
      await updateMerchantTerminal(userFiId, userAccountId, terminal.id, formData, token.accessToken);
    } else {
      await createMerchantTerminal(userFiId, userAccountId, formData, token.accessToken);
    }
    await refreshMerchantTerminals();
  }

  if (loading && !platformMetrics && !fiSummary && !merchantSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // Render Merchant Admin view
  if (isMerchantAdmin) {
    return (
      <MerchantAdminOverviewTab
        summary={merchantSummary}
        terminals={merchantTerminals}
        payouts={myPayouts}
        paymentIssues={paymentIssues}
        loading={loading}
        accountId={userAccountId}
        accountName={accountName}
        onNavigateToTab={handleNavigateToTab}
        onSaveTerminal={handleSaveTerminal}
      />
    );
  }

  // Render FI Admin view
  if (isFIAdmin && !isSuperAdmin) {
    return (
      <FIAdminOverviewTab
        fiSummary={fiSummary}
        merchantMetrics={merchantMetrics}
        payouts={myPayouts}
        paymentIssues={paymentIssues}
        loading={loading}
        fiId={userFiId}
        fiName={fiName}
        onNavigateToTab={handleNavigateToTab}
      />
    );
  }

  // Render Super Admin view (default)
  return (
    <OverviewTab
      platformMetrics={platformMetrics}
      fiMetrics={fiMetrics}
      paymentIssues={paymentIssues}
      fiLoading={fiLoading}
      onNavigateToTab={handleNavigateToTab}
    />
  );
}

const mapStateToProps = (state) => ({
  token: getToken(state),
  user: getUser(state),
  account: getAccount(state),
});

export const OverviewPage = connect(mapStateToProps)(OverviewPageComponent);
